import { useState, useEffect, useRef } from 'react';
import { api, formatPrice } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, Loader2, Upload, X, Download, FileSpreadsheet, Image as ImageIcon, AlertCircle, CheckCircle2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const emptyProduct = { name: '', description: '', price: 0, compare_price: '', category_id: '', images: [], featured: false, active: true, stock: 100 };

// ─── Componente galería de imágenes ───────────────────────────────────────────
function ImageGallery({ images, onChange }) {
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef();

    const handleFiles = async (files) => {
        const arr = Array.from(files);
        for (const file of arr) {
            if (images.length >= 5) { toast.warning('Máximo 5 imágenes'); break; }
            setUploading(true);
            try {
                const fd = new FormData();
                fd.append('file', file);
                const r = await api.post('/admin/upload/image', fd);
                onChange([...images, r.data.url]);
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al subir imagen');
            }
            setUploading(false);
        }
    };

    const remove = (i) => onChange(images.filter((_, idx) => idx !== i));

    const onDrop = (e) => {
        e.preventDefault();
        handleFiles(e.dataTransfer.files);
    };

    return (
        <div>
            <div className="grid grid-cols-3 gap-2 mb-2">
                {images.map((url, i) => (
                    <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-cp-border">
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        {i === 0 && (
                            <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] text-center py-0.5 font-semibold">
                                Principal
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => remove(i)}
                            className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow"
                        >
                            <X size={10} />
                        </button>
                    </div>
                ))}

                {images.length < 5 && (
                    <div
                        onClick={() => inputRef.current?.click()}
                        onDrop={onDrop}
                        onDragOver={e => e.preventDefault()}
                        className="aspect-square rounded-lg border-2 border-dashed border-cp-border hover:border-cp-primary hover:bg-cp-bg-alt transition-colors cursor-pointer flex flex-col items-center justify-center gap-1"
                    >
                        {uploading ? (
                            <Loader2 size={20} className="animate-spin text-cp-primary" />
                        ) : (
                            <>
                                <Upload size={18} className="text-cp-text-muted" />
                                <span className="text-[10px] text-cp-text-muted text-center leading-tight px-1">
                                    Subir imagen
                                </span>
                            </>
                        )}
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={e => handleFiles(e.target.files)}
                            disabled={uploading}
                        />
                    </div>
                )}
            </div>
            <p className="text-[10px] text-cp-text-muted">
                Max 5 imágenes · 5MB c/u · JPG, PNG, WEBP · Arrastrá o clickeá para subir
            </p>
        </div>
    );
}

// ─── Diálogo carga masiva CSV ─────────────────────────────────────────────────
function BulkUploadDialog({ open, onClose, onSuccess }) {
    const [file, setFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const reset = () => { setFile(null); setResult(null); };
    const handleClose = () => { reset(); onClose(); };

    const downloadTemplate = () => {
        const rows = [
            'name,description,price,compare_price,category_slug,featured,active,stock,images',
            'Pelota de Goma,Pelota resistente para perros,3500,,juguetes-perros,true,true,50,"[]"',
            'Collar Reflectante,Collar ajustable con banda reflectante,3200,,accesorios,false,true,70,"[]"',
        ].join('\n');
        const blob = new Blob([rows], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'plantilla_productos.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    const handleImport = async () => {
        if (!file) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post('/admin/products/bulk', fd);
            setResult(r.data);
            if (r.data.created > 0) onSuccess();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al importar');
        }
        setImporting(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-heading flex items-center gap-2">
                        <FileSpreadsheet size={18} /> Carga Masiva de Productos
                    </DialogTitle>
                </DialogHeader>

                {!result ? (
                    <div className="space-y-4 mt-2">
                        {/* Plantilla */}
                        <div className="bg-cp-bg-alt border border-cp-border rounded-xl p-4">
                            <p className="text-sm font-semibold text-cp-text-main mb-1">Formato CSV requerido</p>
                            <p className="text-xs text-cp-text-muted mb-3">
                                Las columnas deben ser: <code className="bg-white px-1 rounded text-cp-primary">name, description, price, compare_price, category_slug, featured, active, stock, images</code>
                            </p>
                            <p className="text-xs text-cp-text-muted mb-3">
                                Los slugs de categoría son: <span className="font-mono">juguetes-perros · juguetes-gatos · alimentos · accesorios</span>
                            </p>
                            <button
                                onClick={downloadTemplate}
                                className="flex items-center gap-2 text-cp-secondary hover:text-cp-secondary-hover text-xs font-semibold transition-colors"
                            >
                                <Download size={13} /> Descargar plantilla de ejemplo
                            </button>
                        </div>

                        {/* File input */}
                        <div>
                            <Label className="text-sm mb-1.5 block">Archivo CSV</Label>
                            <label className="flex items-center gap-3 border-2 border-dashed border-cp-border hover:border-cp-primary rounded-xl p-4 cursor-pointer transition-colors">
                                <FileSpreadsheet size={24} className="text-cp-text-muted shrink-0" />
                                <div className="min-w-0">
                                    {file ? (
                                        <>
                                            <p className="text-sm font-medium text-cp-text-main truncate">{file.name}</p>
                                            <p className="text-xs text-cp-text-muted">{(file.size / 1024).toFixed(1)} KB</p>
                                        </>
                                    ) : (
                                        <p className="text-sm text-cp-text-muted">Clickeá para seleccionar un archivo .csv</p>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    accept=".csv,text/csv"
                                    className="hidden"
                                    onChange={e => setFile(e.target.files[0] || null)}
                                />
                            </label>
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={handleClose}
                                className="flex-1 border border-cp-border text-cp-text-body hover:bg-cp-bg-alt py-2.5 rounded-xl text-sm font-medium transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleImport}
                                disabled={!file || importing}
                                className="flex-1 bg-cp-secondary hover:bg-cp-secondary-hover disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2"
                            >
                                {importing ? <><Loader2 size={14} className="animate-spin" /> Importando...</> : 'Importar'}
                            </button>
                        </div>
                    </div>
                ) : (
                    /* Resultado */
                    <div className="space-y-4 mt-2">
                        <div className={`flex items-center gap-3 p-4 rounded-xl ${result.created > 0 ? 'bg-cp-success/10' : 'bg-cp-error/10'}`}>
                            {result.created > 0
                                ? <CheckCircle2 size={24} className="text-cp-success shrink-0" />
                                : <AlertCircle size={24} className="text-cp-error shrink-0" />
                            }
                            <div>
                                <p className="font-semibold text-cp-text-main">
                                    {result.created} producto{result.created !== 1 ? 's' : ''} creado{result.created !== 1 ? 's' : ''}
                                </p>
                                {result.errors.length > 0 && (
                                    <p className="text-xs text-cp-text-muted">{result.errors.length} fila{result.errors.length !== 1 ? 's' : ''} con errores</p>
                                )}
                            </div>
                        </div>

                        {result.errors.length > 0 && (
                            <div className="bg-cp-error/5 border border-cp-error/20 rounded-xl p-3 max-h-36 overflow-y-auto">
                                <p className="text-xs font-semibold text-cp-error mb-1">Errores:</p>
                                {result.errors.map((e, i) => (
                                    <p key={i} className="text-xs text-cp-text-muted py-0.5">{e}</p>
                                ))}
                            </div>
                        )}

                        <button
                            onClick={handleClose}
                            className="w-full bg-cp-secondary hover:bg-cp-secondary-hover text-white py-2.5 rounded-xl text-sm font-semibold transition-all"
                        >
                            Cerrar
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

// ─── Diálogo actualización masiva de precios ──────────────────────────────────
function BulkPriceUpdateDialog({ open, onClose, onSuccess }) {
    const [updateType, setUpdateType] = useState('percentage'); // 'percentage' | 'fixed'
    const [value, setValue] = useState('');
    const [updating, setUpdating] = useState(false);

    const handleUpdate = async () => {
        if (!value || isNaN(value)) { toast.error('Ingresa un valor válido'); return; }
        setUpdating(true);
        try {
            await api.post('/admin/products/bulk-price-update', { type: updateType, value: parseFloat(value) });
            toast.success('Precios actualizados correctamente');
            onSuccess();
            onClose();
            setValue('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al actualizar precios');
        }
        setUpdating(false);
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-heading flex items-center gap-2">
                        <TrendingUp size={18} /> Actualización Masiva de Precios
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    <p className="text-sm text-cp-text-muted">
                        Esta acción afectará a <strong>todos</strong> los productos de la tienda.
                    </p>

                    <div className="space-y-4 bg-cp-bg-alt border border-cp-border rounded-xl p-4">
                        <div>
                            <Label className="text-sm mb-1.5 block">Tipo de incremento</Label>
                            <Select value={updateType} onValueChange={setUpdateType}>
                                <SelectTrigger className="bg-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                                    <SelectItem value="fixed">Monto fijo ($)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-sm mb-1.5 block">
                                {updateType === 'percentage' ? 'Porcentaje a sumar (ej: 10)' : 'Monto a sumar (ej: 5000)'}
                            </Label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-cp-text-muted text-sm">
                                    {updateType === 'percentage' ? '%' : '$'}
                                </span>
                                <Input
                                    type="number"
                                    value={value}
                                    onChange={e => setValue(e.target.value)}
                                    className="pl-7 bg-white"
                                    placeholder="0"
                                />
                            </div>
                            <p className="text-[10px] text-cp-text-muted mt-1.5">
                                Tip: Podes usar valores negativos (ej: -10) para bajar los precios.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="flex-1 border border-cp-border text-cp-text-body hover:bg-cp-bg-alt py-2.5 rounded-xl text-sm font-medium transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleUpdate}
                            disabled={updating || !value}
                            className="flex-1 bg-cp-secondary hover:bg-cp-secondary-hover disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2"
                        >
                            {updating ? <><Loader2 size={14} className="animate-spin" /> Procesando...</> : 'Aplicar a todo'}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [priceOpen, setPriceOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyProduct);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const [p, c] = await Promise.all([api.get('/admin/products'), api.get('/admin/categories')]);
            setProducts(p.data);
            setCategories(c.data);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(emptyProduct); setDialogOpen(true); };
    const openEdit = (p) => {
        setEditing(p.id);
        setForm({
            name: p.name, description: p.description, price: p.price,
            compare_price: p.compare_price || '', category_id: p.category_id,
            images: p.images || [], featured: p.featured, active: p.active, stock: p.stock || 0,
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.name || !form.category_id || !form.price) {
            toast.error('Completa nombre, categoría y precio');
            return;
        }
        setSaving(true);
        const payload = {
            ...form,
            price: Number(form.price),
            compare_price: form.compare_price ? Number(form.compare_price) : null,
            stock: Number(form.stock),
        };
        try {
            if (editing) {
                await api.put(`/admin/products/${editing}`, payload);
                toast.success('Producto actualizado');
            } else {
                await api.post('/admin/products', payload);
                toast.success('Producto creado');
            }
            setDialogOpen(false);
            load();
        } catch { toast.error('Error al guardar'); }
        setSaving(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este producto?')) return;
        try { await api.delete(`/admin/products/${id}`); toast.success('Eliminado'); load(); }
        catch { toast.error('Error al eliminar'); }
    };

    const getCatName = (id) => categories.find(c => c.id === id)?.name || '-';

    return (
        <div className="space-y-6" data-testid="admin-products">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h1 className="text-2xl font-heading font-bold text-cp-text-main">Productos</h1>
                <div className="flex gap-2">
                    <button
                        onClick={() => setPriceOpen(true)}
                        className="flex items-center gap-2 bg-cp-accent hover:bg-cp-accent-dark text-cp-text-main px-4 py-2.5 rounded-xl text-sm font-semibold transition-all"
                    >
                        <TrendingUp size={15} /> Actualizar Precios
                    </button>
                    <button
                        onClick={() => setBulkOpen(true)}
                        className="flex items-center gap-2 bg-cp-bg-alt hover:bg-cp-surface border border-cp-border text-cp-text-body px-4 py-2.5 rounded-xl text-sm font-semibold transition-all"
                    >
                        <FileSpreadsheet size={15} /> Carga Masiva
                    </button>
                    <button
                        onClick={openNew}
                        data-testid="add-product-btn"
                        className="flex items-center gap-2 bg-cp-secondary hover:bg-cp-secondary-hover text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all"
                    >
                        <Plus size={16} /> Agregar
                    </button>
                </div>
            </div>

            {/* Tabla */}
            <div className="bg-white border border-cp-border rounded-xl overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>
                ) : products.length === 0 ? (
                    <div className="p-12 text-center">
                        <ImageIcon size={32} className="mx-auto text-cp-text-muted mb-3" />
                        <p className="text-cp-text-muted text-sm">No hay productos todavía</p>
                        <button onClick={openNew} className="mt-3 text-cp-secondary text-sm font-semibold hover:underline">
                            Crear el primero
                        </button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="products-table">
                            <thead>
                                <tr className="border-b border-cp-border-soft bg-cp-bg-alt">
                                    <th className="text-left p-3 text-cp-text-muted font-medium">Producto</th>
                                    <th className="text-left p-3 text-cp-text-muted font-medium hidden md:table-cell">Categoría</th>
                                    <th className="text-right p-3 text-cp-text-muted font-medium">Precio</th>
                                    <th className="text-center p-3 text-cp-text-muted font-medium hidden sm:table-cell">Stock</th>
                                    <th className="text-center p-3 text-cp-text-muted font-medium hidden sm:table-cell">Estado</th>
                                    <th className="text-right p-3 text-cp-text-muted font-medium">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map(p => (
                                    <tr key={p.id} className="border-b border-cp-border-soft last:border-0 hover:bg-cp-bg-alt/50">
                                        <td className="p-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-cp-surface-soft rounded-lg overflow-hidden shrink-0 flex items-center justify-center">
                                                    {p.images?.[0]
                                                        ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" />
                                                        : <ImageIcon size={14} className="text-cp-text-muted" />
                                                    }
                                                </div>
                                                <div>
                                                    <p className="font-medium text-cp-text-main text-sm">{p.name}</p>
                                                    {p.featured && <span className="text-xs text-cp-primary font-semibold">Destacado</span>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-3 text-cp-text-body hidden md:table-cell">{getCatName(p.category_id)}</td>
                                        <td className="p-3 text-right font-semibold text-cp-brand">{formatPrice(p.price)}</td>
                                        <td className="p-3 text-center text-cp-text-body hidden sm:table-cell">{p.stock}</td>
                                        <td className="p-3 text-center hidden sm:table-cell">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.active ? 'bg-cp-success/10 text-cp-success' : 'bg-cp-error/10 text-cp-error'}`}>
                                                {p.active ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td className="p-3 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="p-2 text-cp-text-muted hover:text-cp-secondary rounded-lg hover:bg-cp-bg-alt">
                                                    <Pencil size={14} />
                                                </button>
                                                <button onClick={() => handleDelete(p.id)} data-testid={`delete-product-${p.id}`} className="p-2 text-cp-text-muted hover:text-cp-error rounded-lg hover:bg-cp-bg-alt">
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Dialog crear / editar */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{editing ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label className="text-sm mb-1.5 block">Nombre *</Label>
                            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="product-form-name" />
                        </div>
                        <div>
                            <Label className="text-sm mb-1.5 block">Descripción</Label>
                            <textarea
                                rows={3}
                                value={form.description}
                                onChange={e => setForm({...form, description: e.target.value})}
                                data-testid="product-form-desc"
                                className="w-full px-3 py-2 border border-cp-border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cp-primary/30"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-sm mb-1.5 block">Precio *</Label>
                                <Input type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} data-testid="product-form-price" />
                            </div>
                            <div>
                                <Label className="text-sm mb-1.5 block">Precio anterior</Label>
                                <Input type="number" value={form.compare_price} onChange={e => setForm({...form, compare_price: e.target.value})} />
                            </div>
                        </div>
                        <div>
                            <Label className="text-sm mb-1.5 block">Categoría *</Label>
                            <Select value={form.category_id} onValueChange={v => setForm({...form, category_id: v})}>
                                <SelectTrigger data-testid="product-form-category"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                                <SelectContent>
                                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Galería de imágenes */}
                        <div>
                            <Label className="text-sm mb-1.5 block">Imágenes del producto</Label>
                            <ImageGallery
                                images={form.images}
                                onChange={imgs => setForm({...form, images: imgs})}
                            />
                        </div>

                        <div>
                            <Label className="text-sm mb-1.5 block">Stock</Label>
                            <Input type="number" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} />
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                                <Switch checked={form.featured} onCheckedChange={v => setForm({...form, featured: v})} data-testid="product-form-featured" />
                                <Label className="text-sm">Destacado</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch checked={form.active} onCheckedChange={v => setForm({...form, active: v})} />
                                <Label className="text-sm">Activo</Label>
                            </div>
                        </div>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            data-testid="product-form-save"
                            className="w-full bg-cp-secondary hover:bg-cp-secondary-hover disabled:opacity-60 text-white py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                        >
                            {saving ? <><Loader2 size={15} className="animate-spin" /> Guardando...</> : editing ? 'Actualizar Producto' : 'Crear Producto'}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog carga masiva */}
            <BulkUploadDialog
                open={bulkOpen}
                onClose={() => setBulkOpen(false)}
                onSuccess={() => { load(); toast.success('Productos importados'); }}
            />

            <BulkPriceUpdateDialog
                open={priceOpen}
                onClose={() => setPriceOpen(false)}
                onSuccess={load}
            />
        </div>
    );
}
